import React, { useState } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import { CLIENT_PORTAL_URL, META_DESCRIPTION, META_URL } from "@app/utilities/constants";
import { isBot } from "@app/utilities/helpers";
import PortalLinkFormComp from "@components/forms/PortalLinkFormComp";
import { serverHttp } from "@app/http/base";
import { HttpError } from "@models/HttpError";

interface IPortalPage {
  isCrawler: boolean;
}

enum EmailInvitationStatus {
  ERROR = -1,
  NOT_SENT = 0,
  SENT = 1,
}

const ContactsPage: React.FC<IPortalPage> = (props: IPortalPage) => {
  const [formLoading, setFormLoading] = useState(false),
    [formValues, setFormValues] = useState({ email: "" }),
    [invitationStatus, setInvitationStatus] = useState<EmailInvitationStatus>(EmailInvitationStatus.NOT_SENT);

  const onEmailSubmit = async (formData) => {
    const newFormValues = { ...formValues, ...formData };
    const email = formData["email"];
    setFormValues(newFormValues);

    if (email) {
      setFormLoading(true);
      const { request } = serverHttp({
        method: "GET",
        url: "portal-sign-in",
        params: {
          email,
        },
      });

      request
        .then((res) => {
          const { data } = res;

          if (data) {
            setInvitationStatus(EmailInvitationStatus.SENT);
          } else {
            throw new HttpError("no-email-sent", "Failed to send sign in link", [], 500);
          }
        })
        .catch(() => {
          setInvitationStatus(EmailInvitationStatus.ERROR);
        })
        .finally(() => {
          setFormLoading(false);
        });
    }

    return true;
  };

  /**
   * Meta contents
   */
  const metaTitle = `Client Portal | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/portals`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Client Portal" fluidContainer={false} noPadding={false}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaImage} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaImage} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
      </Head>
      <div className="portal-page common-form" data-testid="portal-page">
        {invitationStatus === EmailInvitationStatus.NOT_SENT ? (
          <div className="wrapper">
            <h2 className="text-center text-capitalize">
              <i className="fa fa-envelope fa-3x d-block mb-5" />
              Instant sign in
            </h2>
            <p className="text-center mb-10">Get a link sent to your inbox to sign in—no password necessary.</p>
            <PortalLinkFormComp name="client-portal-form" isLoading={formLoading} onSubmit={onEmailSubmit} />
          </div>
        ) : invitationStatus === EmailInvitationStatus.SENT ? (
          <div className="wrapper">
            <h2 className="text-center text-capitalize mb-8">
              <i className="fa fa-shipping-fast fa-3x d-block mb-5" />
              Your instant sign-in link is on the way
            </h2>
            <p className="text-center mb-3">
              If an account for <strong>{formValues.email}</strong> exists, you'll receive an email with the link.
            </p>
            <p className="text-center mb-10">The link expires in 24 hours and can only be used once.</p>
          </div>
        ) : (
          <div className="wrapper">
            <h2 className="text-center text-capitalize">
              <i className="fa fa-times-circle fa-3x d-block mb-5" />
              Failed to send the link
            </h2>
            <p className="text-center mb-10">
              Please try to sign in from&nbsp;
              <a aria-label="Client Portal" aria-hidden="false" href={CLIENT_PORTAL_URL}>
                <strong>
                  <u>our client portal</u>
                </strong>
              </a>
              &nbsp;instead.
            </p>
          </div>
        )}
        <div data-testid="signup-help" className="wrapper text-center mt-5">
          <p>
            New client? Call&nbsp;
            <a aria-label="Sign up contact" aria-hidden="false" href="tel:+12143632345">
              <strong>(214) 363-2345</strong>
            </a>
            &nbsp;to request an appointment.
          </p>
        </div>
      </div>
    </PageLayoutV1>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const uA = (req?.headers && req.headers["user-agent"]) || "";

  return {
    props: {
      isCrawler: isBot(uA),
    } as IPortalPage,
  };
};

export default ContactsPage;
